import { useNavigate } from 'react-router-dom';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import FarmFormFields from 'components/forms/FarmFormFields';

import useFarm from 'hooks/useFarm';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { CreateFarmFormSchema, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CreateFarmForm: FunctionComponent<Props> = ({
    className = '',
    onCancelClose,
    onClose,
    requestingClose,
    testId = TEST_ID
}) => {
    const navigate = useNavigate();
    const { farmDetailsPage } = usePageRoutes();

    const { createFarm, isLoading } = useFarm();
    const { CREATE } = useTranslation();

    const form = useForm<CreateFarmFormSchema>({
        mode: 'onChange'
    });

    const {
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const onSubmit = async (data: CreateFarmFormSchema) => {
        const { client, name } = data;

        const farm = await createFarm({ clientId: client.value, farm: { name } });

        if (farm) {
            onClose?.();
            navigate(farmDetailsPage(farm.id));
        }
    };

    return (
        <VerticalContainer className={`${styles.createFarmForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <FarmFormFields />

                    <FormSubmitPanel
                        buttonText={CREATE}
                        disabled={isLoading}
                        errors={errors}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default CreateFarmForm;
